import {
  type ConnectionEvent,
  configureStore,
  offDatabaseConnectionChange,
  onDatabaseConnectionChange,
} from "@nudge-ai/audio-store";
// External dependencies
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ConfigProvider } from "antd";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

// Internal components
import "./App.css";
import IOSAppBanner from "./components/IOSAppBanner/IOSAppBanner.component";
import LoadingModal from "./components/LoadingModal";
import UpdateNotification from "./components/UpdateNotification/UpdateNotification.component";
import AuthLayout from "./layouts/AuthLayout";
import DefaultLayout from "./layouts/DefaultLayouts";
import UnprotectedHeaderLayout from "./layouts/UnprotectedHeaderLayout";
import Agent from "./pages/agents/Agent";
import AnalyticsPage from "./pages/analytics/analytics.page";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/forget-password.page";
import PasswordResetForm from "./pages/auth/reset-password.page";
import Demo from "./pages/demo/demo.page";
import Baa from "./pages/docs/baa.component";
import Privacy from "./pages/docs/privacy.component";
import Terms from "./pages/docs/terms.component";
import AllNotes from "./pages/home/AllNotes.component";
import ClientNotes from "./pages/home/ClientNotes.component";
import NotesForMobile from "./pages/home/NotesForMobile.component";
import HomePage from "./pages/home/home.page";
import Members from "./pages/members/members.page";
import ThankYou from "./pages/payment/ThankYou";
import TemplateDetailsPage from "./pages/templates/template-details.page";
import TemplatesPage from "./pages/templates/templates.page";

// Hooks and utilities
import { useRootPath } from "./hooks/app.hook";
import { useAppDispatch, useAppSelector } from "./hooks/redux-hooks";
import useUserInfo from "./hooks/useUserInfo";
import { patchUserInfo } from "./service/auth.service";
import { fetchUserData } from "./slices/userSlice";
import { theme } from "./theme/ant-theme";
import { initAnalytics } from "./utils/analytics";
import { logAudioStoreConnection } from "./utils/audioStoreLogger";
import { isReclaimableStorageError, recoverFromStorageQuota } from "./utils/audioStoreQuota";
import { INTERCOM_APP_ID } from "./utils/constants";
import { getBrowserTimezone } from "./utils/timezone";

// Styles
import "./styles/global.scss";
import CallDashboard from "./pages/call/dashboard/call-dashboard.component";
import ClinicianComplianceDetail from "./pages/compliance/ClinicianComplianceDetail.page";
import CliniciansList from "./pages/compliance/CliniciansList.page";
import ComplianceFindingReview from "./pages/compliance/ComplianceFindingReview.page";
import ComplianceManageRules from "./pages/compliance/ComplianceManageRules.page";
import ComplianceNoteReview from "./pages/compliance/ComplianceNoteReview.page";
import ComplianceRuleEdit from "./pages/compliance/ComplianceRuleEdit.page";
import FindingsInbox from "./pages/compliance/FindingsInbox.page";
import RulesProgram from "./pages/compliance/RulesProgram.page";
import OnboardingPage from "./pages/onboarding/onboarding.page";

const GOOGLE_CLIENT_ID = "24618415969-sisnrs3hseguen2scue1bdroiton56mo.apps.googleusercontent.com";

function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { rootPath } = useRootPath();
  const { fetchUserInfo } = useUserInfo();
  const { userInfo } = useAppSelector((state) => state.auth);
  const isRecording = useAppSelector((state) => state.recordings.isRecording);

  const [loading, setLoading] = useState(true);
  const hasInitializedRef = useRef(false);

  const onFetchUserInfo = async () => {
    try {
      await fetchUserInfo();

      // Update timezone only if it's different from stored value
      try {
        const currentTimezone = getBrowserTimezone();
        const storedTimezone = userInfo?.onboarding_data?.timezone;

        if (!storedTimezone || currentTimezone !== storedTimezone) {
          console.log(`Updating timezone from '${storedTimezone}' to '${currentTimezone}'`);
          await patchUserInfo({
            onboarding_data: {
              ...userInfo.onboarding_data,
              timezone: currentTimezone,
            },
          });
        }
      } catch (error) {
        console.error("Error updating timezone:", error);
        // Don't block app initialization if timezone update fails
      }

      return;
    } catch (error) {
      console.error("Error fetching user info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Run once on mount to initialize user data
  // biome-ignore lint: this is intentionally mount-only initialization logic
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      dispatch(fetchUserData());
      onFetchUserInfo();
      if (location.pathname === "/" || location.pathname === "/demo") {
        navigate(rootPath);
      }
    } else if (!token) {
      // Remove sessionid cookie if we don't have a token
      // Resolves a bug where on logout we were not properly removing this cookie.
      // Trying to register/login at this point would cause a csrf failure.
      Cookies.remove("sessionid");

      if (
        location &&
        (location.pathname === "/" || location.pathname === "/home" || location.pathname === "/onboarding")
      ) {
        navigate("/register");
      }

      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Reclaim local audio (Cache + IndexedDB) when a disk-full / quota error escapes
  // our catches as an unhandled rejection.
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isReclaimableStorageError(event.reason)) {
        void recoverFromStorageQuota(null);
      }
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }, []);

  // Cache Storage is the primary local audio-backup tier for all users (the
  // enable_cache_storage_backup rollout is fully ramped). Configure on mount, but
  // never rebuild the store singleton mid-recording — defer until the session
  // stops (isRecording re-runs the effect).
  useEffect(() => {
    if (isRecording) return;
    try {
      configureStore(true);
    } catch {
      // ignore — never let backup configuration interfere with the app
    }
  }, [isRecording]);

  // Prod-only triage breadcrumbs for the local audio backup; unsubscribed on unmount.
  useEffect(() => {
    if (process.env.REACT_APP_SERVER_TYPE !== "prod") return;
    const STATES_TO_LOG = ["closed", "closing", "error", "blocked"];
    const handleConnectionChange = (event: ConnectionEvent) => {
      try {
        if (STATES_TO_LOG.includes(event.state)) logAudioStoreConnection(event);
      } catch {
        // logging must never break a storage operation
      }
    };
    try {
      onDatabaseConnectionChange(handleConnectionChange);
    } catch {
      // ignore — connection logging is best-effort
    }
    return () => {
      try {
        offDatabaseConnectionChange(handleConnectionChange);
      } catch {
        // ignore — best-effort cleanup
      }
    };
  }, []);

  if (loading) return <LoadingModal />;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ConfigProvider theme={theme}>
        <IntercomProvider appId={INTERCOM_APP_ID}>
          <UpdateNotification showAsModal={true} />
          <IOSAppBanner />
          <Routes>
            <Route element={<DefaultLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<PasswordResetForm />} />
            </Route>
            <Route element={<UnprotectedHeaderLayout />}>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/baa" element={<Baa />} />
              <Route path="/demo" element={<Demo />} />
            </Route>
            <Route element={<AuthLayout />}>
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/agents" element={<Agent />} />
              <Route path="/home" element={<HomePage />}>
                <Route index element={<AllNotes />} />
                <Route path="clients/:clientUuid" element={<ClientNotes />} />
              </Route>
              <Route path="/members" element={<Members />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/" element={<HomePage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/call/dashboard" element={<CallDashboard />} />
              <Route path="/compliance" element={<FindingsInbox />} />
              <Route path="/compliance/findings/:findingId" element={<ComplianceFindingReview />} />
              <Route path="/compliance/clinicians" element={<CliniciansList />} />
              <Route path="/compliance/clinicians/:userId" element={<ClinicianComplianceDetail />} />
              <Route path="/compliance/clinicians/:userId/notes/:noteId" element={<ComplianceNoteReview />} />
              <Route path="/compliance/rules" element={<RulesProgram />} />
              <Route path="/compliance/manage-rules" element={<ComplianceManageRules />} />
              <Route path="/compliance/manage-rules/:templateId" element={<ComplianceRuleEdit />} />
            </Route>
            <Route element={<AuthLayout avoidTokenValidation />}>
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/templates/:slug" element={<TemplateDetailsPage />} />
            </Route>
            <Route path="/notesforMobile" element={<NotesForMobile />}>
              <Route index element={<AllNotes />} />
              <Route path="clients/:clientUuid" element={<ClientNotes />} />
            </Route>
          </Routes>
        </IntercomProvider>
      </ConfigProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
